import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { formatSeoTitle } from "@shared/seoTitle";

type PublicSeoConfig = {
  siteUrl?: string | null;
  siteName?: string | null;
  logoUrl?: string | null;
  faviconUrl?: string | null;
  titleTemplate?: string | null;
  defaultTitle?: string | null;
  defaultDescription?: string | null;
  defaultImage?: string | null;
  twitterHandle?: string | null;
  socialLinks?: Array<{ label?: string | null; url?: string | null; iconUrl?: string | null }> | null;
  /**
   * Page path → that page's own screenshot (e.g. `/contact` → `/og/contact.jpg`),
   * for the files that exist. Sent by `/api/seo`; see
   * `server/services/ogImages.ts`.
   */
  pageImages?: Record<string, string> | null;
};

export type SeoProps = {
  title?: string | null;
  description?: string | null;
  image?: string | null;
  canonicalPath?: string | null;
  noindex?: boolean;
  jsonLd?: Record<string, any> | null;
  /**
   * Set false when `title` is the finished search-engine title and must be used
   * verbatim.
   *
   * The site default is to decorate (`Privacy Policy` → `Privacy Policy |
   * BullBuzz`), which is right for entity pages. But the admin panel lets an
   * editor set an explicit SEO title, and the server-rendered snapshot already
   * honours it verbatim (`applyTemplate: false`) — without this the crawler and
   * the browser would report two different titles for the same URL.
   */
  applyTitleTemplate?: boolean;
};

function stripTrailingSlash(url: string): string {
  if (url.length > 1 && url.endsWith("/")) return url.slice(0, -1);
  return url;
}

function normalizePathname(pathname: string): string {
  if (!pathname) return "/";
  const withSlash = pathname.startsWith("/") ? pathname : `/${pathname}`;
  if (withSlash !== "/" && withSlash.endsWith("/")) return withSlash.slice(0, -1);
  return withSlash;
}

function isAbsoluteUrl(url: string): boolean {
  return /^https?:\/\//i.test(url) || url.startsWith("data:") || url.startsWith("blob:");
}

function toAbsoluteUrl(url: string, baseUrl: string): string {
  if (!url) return url;
  if (isAbsoluteUrl(url)) return url;
  if (url.startsWith("//")) {
    const proto = baseUrl.startsWith("https://") ? "https:" : "http:";
    return `${proto}${url}`;
  }
  const base = stripTrailingSlash(baseUrl);
  if (url.startsWith("/")) return `${base}${url}`;
  return `${base}/${url}`;
}

function normalizeSocialLinks(
  raw: PublicSeoConfig["socialLinks"],
): Array<{ label: string; url: string; iconUrl?: string }> {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const links: Array<{ label: string; url: string; iconUrl?: string }> = [];
  for (const item of raw) {
    const label = (item?.label || "").toString().trim();
    const url = (item?.url || "").toString().trim();
    const iconUrl = (item?.iconUrl || "").toString().trim();
    if (!label || !/^https?:\/\//i.test(url)) continue;
    const key = url.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    links.push({ label, url, ...(iconUrl ? { iconUrl } : {}) });
  }
  return links;
}

function withOrganizationJsonLd(
  jsonLd: Record<string, any> | null | undefined,
  organization: Record<string, any>,
) {
  if (!jsonLd || typeof jsonLd !== "object") {
    return { "@context": "https://schema.org", "@graph": [organization] };
  }
  if (Array.isArray((jsonLd as any)["@graph"])) {
    return { ...jsonLd, "@graph": [organization, ...(jsonLd as any)["@graph"]] };
  }
  return { "@context": (jsonLd as any)["@context"] || "https://schema.org", "@graph": [organization, jsonLd] };
}

function ensureMetaTag(selector: string, attrs: Record<string, string>): HTMLMetaElement {
  const existing = document.head.querySelector(selector);
  if (existing && existing instanceof HTMLMetaElement) return existing;
  const meta = document.createElement("meta");
  for (const [k, v] of Object.entries(attrs)) meta.setAttribute(k, v);
  document.head.appendChild(meta);
  return meta;
}

function ensureLinkTag(rel: string): HTMLLinkElement {
  const existing = document.head.querySelector(`link[rel=\"${rel}\"]`);
  if (existing && existing instanceof HTMLLinkElement) return existing;
  const link = document.createElement("link");
  link.setAttribute("rel", rel);
  document.head.appendChild(link);
  return link;
}

function removeAllJsonLdScripts() {
  const nodes = document.head.querySelectorAll('script[type="application/ld+json"][data-seo-jsonld="1"]');
  for (const n of Array.from(nodes)) n.parentElement?.removeChild(n);
}

function upsertJsonLdScript(json: Record<string, any>) {
  removeAllJsonLdScripts();
  const script = document.createElement("script");
  script.setAttribute("type", "application/ld+json");
  script.setAttribute("data-seo-jsonld", "1");
  script.text = JSON.stringify(json);
  document.head.appendChild(script);
}

function applyTitleTemplate(template: string, title: string, siteName: string): string {
  return formatSeoTitle(title, template, siteName);
}

export default function Seo(props: SeoProps) {
  const { data: cfg } = useQuery<PublicSeoConfig>({
    queryKey: ["/api/seo"],
    queryFn: async () => {
      const res = await fetch("/api/seo");
      if (!res.ok) return {};
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  const computed = useMemo(() => {
    const rawSiteUrl = (cfg?.siteUrl || "").toString().trim();
    const siteUrl = stripTrailingSlash(
      /^https?:\/\//i.test(rawSiteUrl) ? rawSiteUrl : window.location.origin,
    );
    // Fallbacks mirror `server/services/publicSeo.ts` (FALLBACK_*). They only
    // apply when `/api/seo` fails, so they must not drift back to the previous
    // site's "FXOnline Global" branding — that string was the cause of the
    // sitewide duplicate title in the crawlable HTML.
    const siteName = (cfg?.siteName || "BullBuzz").toString();
    const template = (cfg?.titleTemplate || "%s | BullBuzz").toString();
    const defaultTitle = (
      cfg?.defaultTitle || "BullBuzz - Forex, Crypto and CFD Market News, Reviews and Tools"
    ).toString();
    const defaultDescription = (
      cfg?.defaultDescription ||
      "Independent forex, crypto and CFD broker reviews, market news, expert advisors, VPS providers and free trading calculators."
    ).toString();
    const defaultImage = (cfg?.defaultImage || "/opengraph.jpg").toString();
    const faviconRaw = (cfg?.faviconUrl || "").toString().trim();
    const logoRaw = (cfg?.logoUrl || "").toString().trim();

    const pathname = normalizePathname(props.canonicalPath || window.location.pathname);
    const canonicalUrl = `${siteUrl}${pathname}`;

    const rawTitle = (props.title || "").toString().trim();
    const title = !rawTitle
      ? defaultTitle
      : props.applyTitleTemplate === false
        ? rawTitle
        : applyTitleTemplate(template, rawTitle, siteName);

    const description = (props.description || "").toString().trim() || defaultDescription;

    /**
     * Explicit image → this page's own screenshot → site default.
     *
     * `cfg.pageImages` is the server's list of per-page screenshots that
     * actually exist on disk (see `server/services/ogImages.ts`). Using it here
     * keeps the browser-rendered `og:image` identical to the one in the
     * server-rendered snapshot — otherwise a JS-rendering crawler and a social
     * scraper would disagree about the same URL.
     */
    const explicitImage = (props.image || "").toString().trim();
    const perPageImage = String((cfg?.pageImages || {})[pathname] || "").trim();
    const imageRaw = explicitImage || perPageImage || defaultImage;
    const image = imageRaw ? toAbsoluteUrl(imageRaw, siteUrl) : "";

    const favicon = faviconRaw ? toAbsoluteUrl(faviconRaw, siteUrl) : "";
    const logo = logoRaw ? toAbsoluteUrl(logoRaw, siteUrl) : "";
    const socialLinks = normalizeSocialLinks(cfg?.socialLinks);
    const sameAs = socialLinks.length
      ? socialLinks.map((link) => link.url)
      : (cfg?.twitterHandle || "").toString().trim()
        ? [`https://twitter.com/${(cfg?.twitterHandle || "").toString().trim().replace(/^@/, "")}`]
        : [];

    return {
      siteUrl,
      siteName,
      canonicalUrl,
      title,
      description,
      image,
      favicon,
      logo,
      sameAs,
      twitterHandle: (cfg?.twitterHandle || "").toString().trim(),
    };
  }, [
    cfg,
    props.canonicalPath,
    props.description,
    props.image,
    props.title,
    props.applyTitleTemplate,
  ]);

  useEffect(() => {
    document.title = computed.title;

    // Favicon
    if (computed.favicon) {
      const icon = ensureLinkTag("icon");
      icon.setAttribute("href", "/site-icon.png");
      icon.setAttribute("type", "image/png");
      icon.setAttribute("sizes", "48x48");
      const shortcut = ensureLinkTag("shortcut icon");
      shortcut.setAttribute("href", "/site-icon.png");
      shortcut.setAttribute("type", "image/png");
      const apple = ensureLinkTag("apple-touch-icon");
      apple.setAttribute("href", "/apple-touch-icon.png");
      apple.setAttribute("sizes", "180x180");
    }

    // Canonical
    const canonical = ensureLinkTag("canonical");
    canonical.setAttribute("href", computed.canonicalUrl);

    // Primary description
    const desc = ensureMetaTag("meta[name=\"description\"]", { name: "description" });
    if (computed.description) desc.setAttribute("content", computed.description);

    // Robots
    const robots = ensureMetaTag("meta[name=\"robots\"]", { name: "robots" });
    robots.setAttribute("content", props.noindex ? "noindex, follow" : "index, follow, max-image-preview:large");

    // Open Graph
    const schemaNodes = Array.isArray(props.jsonLd?.["@graph"]) ? props.jsonLd!["@graph"] : [props.jsonLd];
    const isArticle = schemaNodes.some((node: any) => ["NewsArticle", "Article", "BlogPosting"].includes(node?.["@type"]));
    ensureMetaTag("meta[property=\"og:type\"]", { property: "og:type" }).setAttribute("content", isArticle ? "article" : "website");
    ensureMetaTag("meta[property=\"og:site_name\"]", { property: "og:site_name" }).setAttribute(
      "content",
      computed.siteName,
    );
    ensureMetaTag("meta[property=\"og:title\"]", { property: "og:title" }).setAttribute(
      "content",
      computed.title,
    );
    ensureMetaTag("meta[property=\"og:url\"]", { property: "og:url" }).setAttribute(
      "content",
      computed.canonicalUrl,
    );
    if (computed.description) {
      ensureMetaTag("meta[property=\"og:description\"]", { property: "og:description" }).setAttribute(
        "content",
        computed.description,
      );
    }
    if (computed.image) {
      ensureMetaTag("meta[property=\"og:image\"]", { property: "og:image" }).setAttribute(
        "content",
        computed.image,
      );
    }

    // Twitter
    ensureMetaTag("meta[name=\"twitter:card\"]", { name: "twitter:card" }).setAttribute(
      "content",
      computed.image ? "summary_large_image" : "summary",
    );
    ensureMetaTag("meta[name=\"twitter:title\"]", { name: "twitter:title" }).setAttribute(
      "content",
      computed.title,
    );
    if (computed.description) {
      ensureMetaTag("meta[name=\"twitter:description\"]", { name: "twitter:description" }).setAttribute(
        "content",
        computed.description,
      );
    }
    if (computed.image) {
      ensureMetaTag("meta[name=\"twitter:image\"]", { name: "twitter:image" }).setAttribute(
        "content",
        computed.image,
      );
    }
    if (computed.twitterHandle) {
      ensureMetaTag("meta[name=\"twitter:site\"]", { name: "twitter:site" }).setAttribute(
        "content",
        computed.twitterHandle.startsWith("@") ? computed.twitterHandle : `@${computed.twitterHandle}`,
      );
    }

    const organization: Record<string, any> = {
      "@type": "Organization",
      "@id": `${computed.siteUrl}/#organization`,
      name: computed.siteName,
      url: `${computed.siteUrl}/`,
      ...(computed.logo ? { logo: { "@type": "ImageObject", url: computed.logo } } : {}),
      ...(computed.sameAs.length ? { sameAs: computed.sameAs } : {}),
    };
    upsertJsonLdScript(withOrganizationJsonLd(props.jsonLd, organization));
  }, [computed, props.noindex, props.jsonLd]);

  return null;
}
